//@ts-strict-ignore
import { ReactNode, useContext, useEffect, useState } from 'react';
import ConnectedFormContext from '../../Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import handleSubmit from '../utils/handleSubmit';
import { alertAction } from '../../../utils/parseErrorResponse';
import keys from '../utils/keys';
import { IConfirmationState, useConfirmationPrompt } from './useConfirmationPrompt';
import { useP4ToastController } from '../../../views/system/NotificationsArea';

const getFieldList = (registeredFields: undefined | { [dataid: string]: any }) => {
  const list: Array<any> = [];
  if (!registeredFields) {
    return list;
  }
  const keySeq = keys(registeredFields);
  return keySeq.reduce((acc, key) => {
    acc.push(key);
    return acc;
  }, list);
};

//INCOMPLETE TYPE - Please fill in blanks if unexpected errors encountered
export type IActionType<SubmitResult = any> = {
  submitMeta?: {
    /**
     * Suppress 'After Submit' behaviours - This typically just means navigation (I.e. Don't go back to the Workspace).
     */
    suppressAfterSubmit?: boolean;

    /**
     * Suppress 'Post Save' logic - Including document printing, shortages, etc. (Used for SO-Trade Counter Jump).
     */
    suppressPostSave?: boolean;

    /**
     * Suppress the success message only (The action being performed displays its' own, enhanced success message).
     */
    suppressSuccessMessage?: boolean;

    /**
     * Ignore "actions" that can be triggered off the back of save, such as displaying document modals,
     *  because we're about to trigger other behaviours (Such as raising a PO).
     */
    suppressActions?: boolean;
  };
  onSaveComplete?: ({ result }: { result: SubmitResult }) => void;
  onSaveFailed?: (errors?: any) => void;
};

export type IConfirmationTriggeringAction<SubmitResult = any> = (
  action?: IActionType<SubmitResult>,
  values?: any
) => any;

export type IUsePerformSaveType = <SubmitResult = any>(
  onComplete?: (result: SubmitResult, action: any) => void,
  onFailed?: (errors: any) => void
) => [performSave: IConfirmationTriggeringAction<SubmitResult>, confirmationPrompt: ReactNode];

/**
 * @param onComplete - A function to be called when the save has been completed.
 * @param onFailed - A function to be called if the save fails (May be needed to reset certain fields, like re-enabling a 'post' button).
 * Returns[0] a function that will take and handle an action object (I.e. As is used for action buttons - Though can use it for extended purposes now we've separated this logic).
 * Returns[1] a confirmation modal that needs to be placed into the render tree appropriately, so that confirmation prompts are integrated into the save.
 */
export const usePerformSave: IUsePerformSaveType = (onComplete, onFailed) => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const [completionState, setCompletionState] = useState<{ submitPromiseResult: any; action: IActionType } | null>(
    null
  );
  const [confirmationState, setConfirmationState] = useState<IConfirmationState | null>(null);

  //When we do pre-saving, we set a state field, then have an effect to trigger the followup code.
  //This is to ensure the followup code is up-to-date from the redux store/etc.
  useEffect(() => {
    if (!completionState) return;
    const { submitPromiseResult, action } = completionState;

    //Trigger connectedForm submitCompleted first for Job Management purposes.
    connectedForm.submitFunctions?.submitCompleted({ result: submitPromiseResult, action: action });
    if (submitPromiseResult) {
      onComplete?.(submitPromiseResult, action);
    }

    action?.onSaveComplete?.({ result: submitPromiseResult });
    // Effect explicitly intent to run when completionState changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completionState]);

  const toastProps = useP4ToastController();

  const performSave: IConfirmationTriggeringAction = (action: IActionType = {}, values) => {
    connectedForm.setSubmitInProgress?.(true);
    const fields = getFieldList(connectedForm.registeredFields);
    const submitPromise = handleSubmit(
      {
        ...connectedForm,
        setSubmitSucceeded: () => {},
        onSubmitSuccess: (result: any) => {
          setCompletionState({ submitPromiseResult: result, action });
        },
        onSubmitFailed: (errors: any) => {
          onFailed?.(errors);
          action?.onSaveFailed?.(errors);
          //Call the standard connected form submit failed button to reset 'submitInProgress' flag.
          //Passing 'alertRequired' metaData so we don't report the error twice to the user.
          connectedForm.submitFunctions?.submitFailed(errors, { alertRequired: false });
        },
        values: values ? values : connectedForm.values,
        customAlertHandler: (_: unknown, meta: any, title: string, message: string, mutationProps: any) => {
          if (meta.confirmationReference) {
            setConfirmationState({
              action,
              meta,
              title,
              message,
              // If values were overwritten, copy into state - We need to manage it slightly differently.
              submittedValues: values ? values : null,
            });
            return;
          }
          //If no custom handling, call the default handling.
          alertAction({ dispatch, entity: connectedForm?.entity }, meta, title, message, mutationProps);
        },
        toastProps: toastProps,
      },
      fields,
      'saved',
      { suppressSuccessMessage: true, suppressAfterSubmit: true }
    );

    return submitPromise.then(() => {}, connectedForm.submitFunctions?.submitFailed);
  };
  const confirmationPrompt = useConfirmationPrompt(performSave, confirmationState, setConfirmationState);

  return [performSave, confirmationPrompt];
};
